'use client';

import { useEffect, useRef } from 'react';
import { isDedicatedAppHost } from '@/lib/appHostUtils';
import capacitorDeviceService from '@/lib/capacitorDeviceService';
import { isStandalonePwa } from '@/lib/pwaAppIdentity';

const AUTH_READY_FALLBACK_MS = 800;
const APP_SHELL_READY_FALLBACK_MS = 2500;

function isNativeCapacitorSession(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(
    (window as any).Capacitor?.isNativePlatform?.() ||
    /CapacitorAndroid|CapacitoriOS/.test(navigator.userAgent) ||
    window.location?.protocol === 'capacitor:'
  );
}

function isStartupRecoverySession(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return typeof window.__markStartupSuccess === 'function'
    || Boolean(document.getElementById('capacitor-startup-overlay'))
    || isStandalonePwa()
    || isDedicatedAppHost(window.location.hostname);
}

function coldStartLog(tag: string, msg: string, data?: unknown) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.__coldStartLog?.(tag, msg, data);
  } catch {
    // Never let diagnostics break startup.
  }
}

/**
 * CapacitorStartupSuccess
 * 
 * This component marks the app as successfully loaded when it mounts.
 * This is used by the startup recovery script to detect if the app crashed.
 * Also initializes global deep link listener for handling app URL opens.
 * Also checks for pending message navigation (from FCM notification tap when app was cold-started).
 * 
 * DEEP LINK RECOVERY:
 * Deep links that arrive during cold start (before React is ready) are captured by
 * capacitor-startup.js and stored in sessionStorage. This component processes them
 * once React is mounted and auth state is available.
 * 
 * Should be placed inside providers but rendered early in the component tree.
 * 
 * IMPORTANT: This component returns null and only runs effects on the client
 * to avoid hydration mismatches.
 */
export default function CapacitorStartupSuccess() {
  const startupSuccessMarkedRef = useRef(false);

  // Mark startup success and initialize deep link listener after mount
  useEffect(() => {
    const isNativeApp = isNativeCapacitorSession();
    const startupRecoveryActive = isStartupRecoverySession();

    if (!startupRecoveryActive) return;

    coldStartLog('REACT_BOOT', 'CapacitorStartupSuccess mounted', {
      path: window.location.pathname,
      href: window.location.href,
      perfNow: Math.round(performance.now()),
      readyAlreadySignaled: !!window.__taStartupScreenReadySignaled,
      isNativeApp,
      startupRecoveryActive,
    });

    if (isNativeApp) {
      // Initialize deep link listener EARLY - this will also process any pending deep links
      // that were captured by capacitor-startup.js before React was ready
      coldStartLog('REACT_BOOT', 'Initializing deep link listener');
      capacitorDeviceService.initDeepLinkListener().catch(err => {
        coldStartLog('REACT_BOOT', 'Failed to init deep link listener', { error: err?.message || String(err) });
        console.warn('[CapacitorStartup] Failed to init deep link listener:', err);
      });

      // Process persisted deep links (from localStorage, survives app crashes)
      // This catches deep links that were persisted but not processed due to slow cold start
      coldStartLog('REACT_BOOT', 'Processing persisted deep links');
      capacitorDeviceService.processPersistedDeepLinks().catch(err => {
        coldStartLog('REACT_BOOT', 'Failed to process persisted deep links', { error: err?.message || String(err) });
        console.warn('[CapacitorStartup] Failed to process persisted deep links:', err);
      });

      // Also try to process pending deep links directly (redundant but ensures coverage)
      coldStartLog('REACT_BOOT', 'Processing pending deep links');
      capacitorDeviceService.processPendingDeepLinks().catch(err => {
        coldStartLog('REACT_BOOT', 'Failed to process pending deep links', { error: err?.message || String(err) });
        console.warn('[CapacitorStartup] Failed to process pending deep links:', err);
      });
    }
    
    // Check for pending message navigation (from FCM notification tap during cold start)
    // NOTE: This is now mostly handled by capacitor-init.js event queuing
    // but we keep this as a fallback for backwards compatibility
    const checkPendingMessageNavigation = () => {
      try {
        // Check both sessionStorage and localStorage (new persistent storage)
        let pendingNav = sessionStorage.getItem('pending_message_navigation');
        if (!pendingNav) {
          pendingNav = localStorage.getItem('deeplink_pending_message_navigation');
        }
        
        if (pendingNav) {
          const { threadId, threadName, timestamp } = JSON.parse(pendingNav);
          // Only process if within last 60 seconds (increased from 30 for slow cold starts)
          if (Date.now() - timestamp < 60000) {
            console.log('[CapacitorStartup] 📬 Found pending message navigation, thread_id:', threadId);
            
            // Clear BOTH storage locations
            sessionStorage.removeItem('pending_message_navigation');
            localStorage.removeItem('deeplink_pending_message_navigation');
            
            // Check if user is logged in first (has auth token)
            const hasAuth = localStorage.getItem('auth_tokens') || localStorage.getItem('auth_token');
            if (hasAuth) {
              console.log('[CapacitorStartup] 📬 User authenticated, dispatching message open events');
              
              // Hide any loading overlays first
              if (typeof window.__hideNavigationUI === 'function') {
                window.__hideNavigationUI();
              }
              
              // Dispatch events - these will be queued if listener not ready
              // and replayed when Header.tsx marks listener ready
              setTimeout(() => {
                // First, open the Messages modal
                window.dispatchEvent(new CustomEvent('toggleModal', { 
                  detail: { modalName: 'messages' } 
                }));
                
                // Then, navigate to the specific thread
                if (threadId) {
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('openMessagesModal', { 
                      detail: { 
                        threadId: threadId,
                        threadName: threadName || 'Messages'
                      } 
                    }));
                  }, 200);
                }
              }, 100);
            } else {
              // Store for after login — redirect to dashboard, pending nav in sessionStorage
              console.log('[CapacitorStartup] 📬 User not authenticated, storing for post-login navigation');
              sessionStorage.setItem('post_login_redirect', '/client-zone/dashboard');
            }
          } else {
            // Stale navigation, clear it
            sessionStorage.removeItem('pending_message_navigation');
            localStorage.removeItem('deeplink_pending_message_navigation');
          }
        }
        
        // Also check for pending email navigation
        // Check both sessionStorage and localStorage (new persistent storage)
        let pendingEmailNav = sessionStorage.getItem('pending_email_navigation');
        if (!pendingEmailNav) {
          pendingEmailNav = localStorage.getItem('deeplink_pending_email_navigation');
        }
        
        if (pendingEmailNav) {
          const { messageId, emailId, threadId, folder, accountId, clientId, timestamp } = JSON.parse(pendingEmailNav);
          const resolvedEmailId = emailId || messageId;
          if (Date.now() - timestamp < 60000) { // Increased to 60s for slow cold starts
            console.log('[CapacitorStartup] 📧 Found pending email navigation, email_id:', resolvedEmailId);
            
            // Clear BOTH storage locations
            sessionStorage.removeItem('pending_email_navigation');
            localStorage.removeItem('deeplink_pending_email_navigation');
            
            const hasAuth = localStorage.getItem('auth_tokens') || localStorage.getItem('auth_token');
            if (hasAuth) {
              // Hide any loading overlays first
              if (typeof window.__hideNavigationUI === 'function') {
                window.__hideNavigationUI();
              }
              
              console.log('[CapacitorStartup] 📧 User authenticated, dispatching email open events');
              
              // Dispatch events - these will be queued if listener not ready
              setTimeout(() => {
                // Open the Email modal
                window.dispatchEvent(new CustomEvent('toggleModal', { 
                  detail: { modalName: 'email' } 
                }));
                
                // Then navigate to the specific email
                if (resolvedEmailId) {
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('openEmailModal', { 
                      detail: { 
                        emailId: resolvedEmailId,
                        threadId,
                        folder: folder || 'INBOX',
                        accountId,
                        clientId,
                      } 
                    }));
                  }, 200);
                }
              }, 100);
            } else {
              // Store for after login — redirect to dashboard, pending nav in sessionStorage
              sessionStorage.setItem('post_login_redirect', '/client-zone/dashboard');
            }
          } else {
            sessionStorage.removeItem('pending_email_navigation');
            localStorage.removeItem('deeplink_pending_email_navigation');
          }
        }
      } catch (e) {
        console.error('[CapacitorStartup] Error checking pending navigation:', e);
      }
    };
    
    const markStartupSuccess = () => {
      if (startupSuccessMarkedRef.current) {
        return;
      }

      startupSuccessMarkedRef.current = true;

      coldStartLog('APP_READY', 'Marking startup success', {
        path: window.location.pathname,
        href: window.location.href,
        perfNow: Math.round(performance.now()),
        readyAlreadySignaled: !!window.__taStartupScreenReadySignaled,
      });

      if (typeof window.__markStartupSuccess === 'function') {
        window.__markStartupSuccess();
      } else {
        const overlay = document.getElementById('capacitor-startup-overlay');
        if (overlay) {
          overlay.classList.add('hiding');
          setTimeout(() => overlay.remove(), 300);
        }
      }

      checkPendingMessageNavigation();

      window.__shipColdStartLogs?.({
        trigger: 'app_ready',
        forceAll: true,
      });
    };

    const handleStartupReady = () => {
      coldStartLog('APP_READY_EVENT', 'Received ta-app-screen-ready event', {
        path: window.location.pathname,
        perfNow: Math.round(performance.now()),
      });
      markStartupSuccess();
    };

    window.addEventListener('ta-app-screen-ready', handleStartupReady);

    if (window.__taStartupScreenReadySignaled) {
      coldStartLog('APP_READY_EVENT', 'Ready signal detected before listener registration', {
        path: window.location.pathname,
        perfNow: Math.round(performance.now()),
      });
      markStartupSuccess();
    }

    // Login and other auth screens don't emit dashboard-ready, so allow them to settle
    // briefly and then dismiss the startup overlay if we're on an auth route.
    let authScreenTimer: ReturnType<typeof setTimeout> | null = null;
    let fallbackReadyTimer: ReturnType<typeof setTimeout> | null = null;
    if (window.location.pathname.startsWith('/auth/')) {
      coldStartLog('APP_READY_FALLBACK', 'Arming auth screen fallback timer', {
        path: window.location.pathname,
        delayMs: AUTH_READY_FALLBACK_MS,
      });
      authScreenTimer = setTimeout(() => {
        coldStartLog('APP_READY_FALLBACK', 'Auth screen fallback timer fired', {
          path: window.location.pathname,
          perfNow: Math.round(performance.now()),
        });
        markStartupSuccess();
      }, AUTH_READY_FALLBACK_MS);
    } else {
      coldStartLog('APP_READY_FALLBACK', 'Arming app shell fallback timer', {
        path: window.location.pathname,
        delayMs: APP_SHELL_READY_FALLBACK_MS,
      });
      fallbackReadyTimer = setTimeout(() => {
        coldStartLog('APP_READY_FALLBACK', 'App shell fallback timer fired', {
          path: window.location.pathname,
          perfNow: Math.round(performance.now()),
          readyAlreadySignaled: !!window.__taStartupScreenReadySignaled,
        });
        markStartupSuccess();
      }, APP_SHELL_READY_FALLBACK_MS);
    }
    
    return () => {
      window.removeEventListener('ta-app-screen-ready', handleStartupReady);
      if (authScreenTimer) {
        clearTimeout(authScreenTimer);
      }
      if (fallbackReadyTimer) {
        clearTimeout(fallbackReadyTimer);
      }
    };
  }, []);
  
  // Always return null - this component is invisible
  return null;
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    __markStartupSuccess?: () => void;
    __clearAppDataAndReload?: () => Promise<void>;
    __coldStartLog?: (tag: string, msg: string, data?: unknown) => void;
    __hideNavigationUI?: () => void;
    __hideCallConnectingUI?: () => void;
    __shipColdStartLogs?: (options?: { trigger?: string; forceAll?: boolean; keepLogs?: boolean }) => void;
    __taStartupScreenReadySignaled?: boolean;
  }
}
